import React from 'react';
import { connect } from 'react-redux';
import SeoHeaders from "../components/SeoHeaders";

function Subprocessors() {
  return (
    <div className="relative py-16 bg-white overflow-hidden">
      <SeoHeaders
        title={"Sub-Processors | Data Processing Partners | Buddy"}
        description={"Buddy's sub-processors and data processing partners. Learn about how we manage third-party data processing in compliance with UK and EU GDPR."}
      />
      
      <div className="hidden lg:block lg:absolute lg:inset-y-0 lg:h-full lg:w-full">
        <div className="relative h-full text-lg max-w-prose mx-auto">
          <svg className="absolute top-12 left-full transform translate-x-32" width="404" height="384" fill="none" viewBox="0 0 404 384">
            <defs>
              <pattern id="74b3fd99-0a6f-4271-bef2-e80eeafdf357" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <rect x="0" y="0" width="4" height="4" className="text-gray-200" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="404" height="384" fill="url(#74b3fd99-0a6f-4271-bef2-e80eeafdf357)" />
          </svg>
          <svg className="absolute top-1/2 right-full transform -translate-y-1/2 -translate-x-32" width="404" height="384" fill="none" viewBox="0 0 404 384">
            <defs>
              <pattern id="f210dbf6-a58d-4871-961e-36d5016a0f49" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <rect x="0" y="0" width="4" height="4" className="text-gray-200" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="404" height="384" fill="url(#f210dbf6-a58d-4871-961e-36d5016a0f49)" />
          </svg>
          <svg className="absolute bottom-12 left-full transform translate-x-32" width="404" height="384" fill="none" viewBox="0 0 404 384">
            <defs>
              <pattern id="d3eb07ae-5182-43e6-857d-35c643af9034" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <rect x="0" y="0" width="4" height="4" className="text-gray-200" fill="currentColor" />
              </pattern>
            </defs>
            <rect width="404" height="384" fill="url(#d3eb07ae-5182-43e6-857d-35c643af9034)" />
          </svg>
        </div>
      </div>
      
      <div className="relative px-4 sm:px-6 lg:px-8">
        <div className="text-lg max-w-prose mx-auto mb-6">
          <p className="text-base text-center leading-6 text-orange-600 font-semibold tracking-wide uppercase">Legal</p>
          <h1 className="mt-2 mb-8 text-3xl text-center leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl sm:leading-10">Buddy Sub-Processors and Data Processing Partners</h1>
        </div>
        
        <div className="prose prose-md text-gray-500 mx-auto">
          <p className="text-lg text-gray-600 mb-6">
            Before listing our sub-processors, we aim to provide clear information about how Buddy manages and discloses the third parties that may process data on our behalf. This transparency supports our obligations under both the UK and EU GDPR and helps customers understand how we protect their information.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Buddy Sub-Processors and Data Processing Partners</h2>
          <p className="text-sm text-gray-500 mb-6"><strong>Last updated:</strong> October 2025</p>
          
          <p className="mb-6">
            Buddy uses a limited number of trusted third-party service providers ("sub-processors") to support delivery of its payroll and HR platform. These providers may process limited customer data on our behalf, strictly under our instructions and in full compliance with the <strong>UK General Data Protection Regulation (UK GDPR)</strong> and the <strong>EU General Data Protection Regulation (EU GDPR)</strong>.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Core Sub-Processors</h3>
          
          <div className="overflow-x-auto mb-8">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Sub-Processor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Purpose of Processing</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Categories of Data</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Data Location / Transfer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Where Used</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-300"><strong>Amazon Web Services EMEA SARL (AWS)</strong></td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Cloud infrastructure — application hosting, storage, and backups (e.g. Lambda, DynamoDB, S3)</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Customer and employee data, files, logs</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Primary: Frankfurt (eu-central-1); encrypted backups in other EU regions</td>
                  <td className="px-4 py-3 text-sm text-gray-500">Core platform hosting</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-300"><strong>Amazon Simple Email Service (SES)</strong></td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Transactional email delivery (password resets, approvals, notifications)</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Recipient email addresses, message content</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">EU (Ireland – via AWS SES region)</td>
                  <td className="px-4 py-3 text-sm text-gray-500">System notifications</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-300"><strong>ActiveCampaign LLC</strong></td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Marketing email delivery and contact management</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Prospect / customer contact details</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">EU servers with SCCs for US back-up</td>
                  <td className="px-4 py-3 text-sm text-gray-500">Marketing communications</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-300"><strong>Google Workspace (Google Ireland Ltd.)</strong></td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Internal email, document storage and collaboration</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Customer correspondence, support attachments</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">EU data region (+ US backup under SCCs)</td>
                  <td className="px-4 py-3 text-sm text-gray-500">Internal operations</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-300"><strong>Slack Technologies Ltd.</strong></td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Internal team messaging</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Limited user names and internal discussions</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">US / EU regions (SCCs)</td>
                  <td className="px-4 py-3 text-sm text-gray-500">Internal communications</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-300"><strong>ClickUp Inc.</strong></td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Internal project and task management</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Limited project/task data referencing customers</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">US (SCCs)</td>
                  <td className="px-4 py-3 text-sm text-gray-500">Internal operations</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-300"><strong>Zendesk Inc.</strong></td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Customer support ticketing and chat</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Contact details, support messages, attachments</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">EU / US regions (SCCs)</td>
                  <td className="px-4 py-3 text-sm text-gray-500">Customer support</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-300"><strong>Stripe Payments Europe Ltd.</strong></td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Subscription billing and payment processing</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Customer billing data, company contact details</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Ireland (EU)</td>
                  <td className="px-4 py-3 text-sm text-gray-500">Payment processing</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-300"><strong>Fyorin Ltd.</strong></td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Fintech platform for business payments and collections</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Company billing data, payment records</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Malta / UK</td>
                  <td className="px-4 py-3 text-sm text-gray-500">Financial transactions</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-300"><strong>UiPath Inc.</strong></td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Desktop automation for data migration (executed on Buddy-controlled devices)</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Payroll data files processed locally</td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-r border-gray-300">Local (company machines only)</td>
                  <td className="px-4 py-3 text-sm text-gray-500">Migration automation</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Affiliated Entity Processing</h3>
          <p className="mb-6">
            Buddy Limited (Malta) and Buddy UK Operations Limited (UK) are <strong>affiliated entities under common ownership</strong> that share responsibilities for certain processing activities. Each entity may act as a data controller for its local operations while cooperating under a mutual data-sharing agreement. This agreement defines the legal basis for data exchanges, such as the performance of contractual obligations and legitimate interests in providing seamless customer support, onboarding, and marketing activities. Data is shared only where necessary, under safeguards ensuring compliance with <strong>UK GDPR</strong> and <strong>EU GDPR</strong>, including equivalent security and confidentiality standards.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Services Not Considered Sub-Processors</h3>
          <p className="mb-6">
            Certain integrations (e.g. <strong>Xero</strong>, <strong>FreeAgent</strong>, and similar accounting systems) are used only when authorised by the customer via API connections. In such cases, Buddy acts as a data controller transmitting data directly to another controller, so these third parties are <strong>not</strong> considered Buddy sub-processors.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">International Data Transfers</h3>
          <p className="mb-4">
            Where a sub-processor is located outside the United Kingdom or European Economic Area (EEA), Buddy ensures that any data transfer is carried out in accordance with <strong>Chapter V</strong> of the EU GDPR and the corresponding provisions of the <strong>UK GDPR</strong>. Appropriate safeguards are applied, including:
          </p>
          <ul className="list-disc pl-6 mb-6">
            <li>The <strong>EU Standard Contractual Clauses (SCCs)</strong> issued by the European Commission; and/or</li>
            <li>The <strong>UK International Data Transfer Addendum (IDTA)</strong> as approved by the UK Information Commissioner's Office (ICO).</li>
          </ul>
          <p className="mb-6">
            These mechanisms ensure that all personal data remains protected to the same standard as within the EEA and UK.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Security and Oversight</h3>
          <p className="mb-6">
            All sub-processors are vetted to ensure they maintain security, confidentiality, and compliance standards consistent with Buddy's own obligations under the <strong>UK GDPR</strong>, <strong>EU GDPR</strong>, and the <strong>Data Protection Act 2018</strong>. Regular reviews and risk assessments are carried out to verify ongoing compliance.
          </p>

          <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">Updates</h3>
          <p className="mb-6">
            Buddy reviews this sub-processor list periodically. Material changes or additions will be communicated to customers in advance, in accordance with Buddy's Data Processing Agreement.
          </p>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              © 2025 Buddy Limited & Buddy UK Operations Limited. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    // Add any state mappings if needed
  }
}

const mapDispatchToProps = dispatch => ({
  // Add any dispatch mappings if needed
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Subprocessors)
